import { filter, map, take } from 'rxjs/operators';
import { ChangeDetectionStrategy, Component, OnInit, ViewContainerRef } from '@angular/core';
import { NavigationStart, Router } from '@angular/router'

import { Observable } from 'rxjs'
import { select, Store } from '@ngrx/store'
import { TranslateService } from '@ngx-translate/core'

import * as user from '../../../auth/actions/user'
import * as pages from '../../actions/pages.actions'
import * as menus from '../../actions/menus.actions'
import * as cart from '../../../cart/actions/cart'
import * as status from '../../../base/actions/status.actions'
import * as product from '../../../catalog/actions/product'

import * as fromBase from '../../reducers'
import * as fromAuth from '../../../auth/reducers'
import * as fromCart from '../../../cart/reducers'
import * as fromCatalog from '../../../catalog/reducers'

import { environment } from '../../../../environments/environment'

import { UserContext } from '../../../auth/models/auth';
import { CartStats } from '../../../cart/models/cart-models'

import { ThemeConfig } from '../../../core/helpers/theme-config'

import { getFromUrlTerm, makeSearchTermUrl } from '../../../core/helpers/url-ready'

import { UXPage } from '../../../core/models/ux-page';
import { CrashService } from '../../../core/services/crash.service'

import * as moment from 'moment'
import { MenuItem } from '../../models/menu-items';
import { Crumb } from '../../../core/models/crumb';
import { PageRoutingService } from '../../../core/services/page-routing.service';
import chaining from '../../../core/helpers/chaining';
import { Setting } from '../../../core/models/setting';

const viewMode = ThemeConfig.viewMode
const catalogRouteName = ThemeConfig.get<string>('catalog', 'route-name')
const logoUrl = ThemeConfig.get<string>('app', 'logo')

@Component({
  selector: 'app-base-container',
  templateUrl: './base-container.component.html',
  styleUrls: ['./base-container.component.scss'],
  //changeDetection: ChangeDetectionStrategy.OnPush
})
export class BaseContainer implements OnInit {
  languages: { [code: string]: string }[]
  languages$: Observable<string[]>

  isCustomerSelected$: Observable<boolean>
  isSalesRep$: Observable<boolean>
  isAdmin$: Observable<boolean>
  isLoggedIn$: Observable<boolean>
  cart$: Observable<CartStats>
  currency$: Observable<string>
  user$: Observable<UserContext>

  isPagesLoading$: Observable<boolean>
  catalogPages$: Observable<UXPage[]>
  menuItems$: Observable<MenuItem[]>

  //crumbs$: Observable<Crumb[]>
  //showCrumbs$: Observable<boolean>
  simplifiedHeader$: Observable<boolean>

  searchTerm$: Observable<string>
  searchTerm: String
  navigateUrl$: Observable<string>

  globalProductsIsLoading$: Observable<boolean>

  tenantSettings$: Observable<Setting>
  topMenuStyle$: Observable<string>

  showHeader: boolean

  constructor(
    private store: Store<fromAuth.State>,
    private router: Router,
    private translate: TranslateService,
    private container: ViewContainerRef,
    private pageRouting: PageRoutingService,
    private crash: CrashService
  ) {
    this.user$ = store.pipe(select(fromAuth.getUser))

    this.languages$ = store.pipe(
      select(fromBase.getSupportedLanguages)
    )

    let browserLang = translate.getBrowserLang()

    // Set user lang or use browser lang
    this.user$.pipe(
      take(1),
      map(user => user && user.preferences && user.preferences.language)
    ).subscribe(lang => {
      translate.use(lang || browserLang)
      moment.locale(lang || browserLang)
    })

    this.languages$.subscribe(languages => {
      this.languages = languages.map(langCode => ({code: langCode, name: `lang.${langCode}` }))

      // Default fallback lang is browser lang
      if (this.languages.find(lang => lang.code === browserLang))
        translate.setDefaultLang(browserLang)
      else {
        browserLang = chaining(this.languages, '[0].code') || 'en'
        translate.setDefaultLang(browserLang)
      }
    })

    this.isLoggedIn$ = store.pipe(select(fromAuth.isLoggedIn))
    this.isCustomerSelected$ = this.store.pipe(select(fromAuth.isCustomerSelected))
    this.isSalesRep$ = this.store.pipe(select(fromAuth.isSalesRep))
    this.isAdmin$ = this.store.pipe(select(fromAuth.isAdmin))
    this.cart$ = store.pipe(select(fromCart.getCartStats))
    this.currency$ = store.pipe(select(fromAuth.getCurrency))
    this.isPagesLoading$ = store.pipe(select(fromBase.isPagesLoading))
    this.catalogPages$ = store.pipe(select(fromBase.getCatalogPages))
    this.menuItems$ = store.pipe(
      select(fromBase.getTopMenuList))
    //this.crumbs$ = pageRouting.breadCrumbs$
    //this.showCrumbs$ = pageRouting.breadcrumbsStatus$
    this.simplifiedHeader$ = pageRouting.simplifiedHeaderStatus$
    //this.tenantSettings$ = store.pipe(select(fromBase.getTenantSettings))

    this.searchTerm$ = store.pipe(
      select(fromCatalog.getSearchTerm),
      filter(term => !!term),
      map(getFromUrlTerm))

    this.searchTerm$.subscribe(term => this.searchTerm = term)

    this.navigateUrl$ = router.events.pipe(
      filter(ev => ev instanceof NavigationStart),
      map((ev: NavigationStart) => ev.url))

    this.globalProductsIsLoading$ = this.store.pipe(
      select(fromCatalog.isGlobalProductsLoading)
    )

    this.store.pipe(
      select(fromBase.showHeader)
    ).subscribe(showHeader => {
      this.showHeader = showHeader
    })

    // When app reloads, force getting of new pages and menus
    this.store.dispatch(new pages.Init(true))
    this.store.dispatch(new menus.Init(true))
    this.store.dispatch(new status.SetHeader(true))
  }

  ngOnInit() {
    this.topMenuStyle$ = this.store.pipe(
      select(fromBase.getTopMenuStyle)
    )
  }

  get logoUrl(): string {
    return logoUrl
  }

  get viewMode(): string {
    return viewMode
  }

  get catalogRoute(): string {
    return catalogRouteName
  }

  onSearch(term: string) {
    this.router.navigate([
      'search',
      term
    ])
  }

  selectCustomer() {
    this.router.navigateByUrl('/customer-selection')
    this.store.dispatch(new cart.Reset())
    this.store.dispatch(new user.SelectCustomer())
  }

  onLangChange(code: string) {
    this.store.dispatch(new user.SetLanguage(code))
  }

  itemClick(): void {
    this.store.dispatch(new product.SetProductDefaultSortOrder())
  }
}
